import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Users, UserCheck, UserX, Clock, MapPin } from "lucide-react";

interface Connection {
  id: string;
  requester_id: string;
  recipient_id: string;
  status: "pending" | "accepted" | "declined";
  created_at: string;
  requester?: {
    id: string;
    full_name: string;
    bio: string;
    location_city: string;
    skills: string[];
  };
  recipient?: {
    id: string;
    full_name: string;
    bio: string;
    location_city: string;
    skills: string[];
  };
}

export default function Connections() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/auth");
        return;
      }

      setCurrentUserId(session.user.id);

      const { data, error } = await supabase
        .from("connections")
        .select(`
          *,
          requester:profiles!connections_requester_id_fkey(id, full_name, bio, location_city, skills),
          recipient:profiles!connections_recipient_id_fkey(id, full_name, bio, location_city, skills)
        `)
        .or(`requester_id.eq.${session.user.id},recipient_id.eq.${session.user.id}`)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setConnections(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to load connections",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const respondToRequest = async (connectionId: string, status: "accepted" | "declined") => {
    setActionLoading(connectionId);
    try {
      const { error } = await supabase
        .from("connections")
        .update({ status })
        .eq("id", connectionId);

      if (error) throw error;

      toast({
        title: status === "accepted" ? "Request Accepted" : "Request Declined",
        description: `Connection request ${status}!`,
      });

      loadConnections();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update connection request",
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const getOtherUser = (connection: Connection) => {
    if (connection.requester_id === currentUserId) {
      return connection.recipient;
    }
    return connection.requester;
  };

  const renderConnectionCard = (connection: Connection, showActions = false) => {
    const otherUser = getOtherUser(connection);
    if (!otherUser) return null;

    return (
      <Card key={connection.id} className="hover:shadow-md transition-shadow">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div
              className="flex items-start gap-4 flex-1 cursor-pointer"
              onClick={() => navigate(`/profile/${otherUser.id}`)}
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">
                {otherUser.full_name?.charAt(0) || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="truncate">{otherUser.full_name || "Anonymous"}</CardTitle>
                {otherUser.location_city && (
                  <CardDescription className="flex items-center gap-1 mt-1">
                    <MapPin className="h-3 w-3" />
                    {otherUser.location_city}
                  </CardDescription>
                )}
              </div>
            </div>
            {showActions && connection.recipient_id === currentUserId && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => respondToRequest(connection.id, "accepted")}
                  disabled={actionLoading === connection.id}
                  className="gap-1"
                >
                  <UserCheck className="h-3 w-3" />
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => respondToRequest(connection.id, "declined")}
                  disabled={actionLoading === connection.id}
                  className="gap-1"
                >
                  <UserX className="h-3 w-3" />
                  Decline
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        {otherUser.bio && (
          <CardContent>
            <p className="text-sm text-muted-foreground line-clamp-2">{otherUser.bio}</p>
            {otherUser.skills && otherUser.skills.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {otherUser.skills.slice(0, 3).map((skill, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {skill}
                  </Badge>
                ))}
                {otherUser.skills.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{otherUser.skills.length - 3}
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  };

  const pendingReceived = connections.filter(
    (c) => c.status === "pending" && c.recipient_id === currentUserId
  );
  const pendingSent = connections.filter(
    (c) => c.status === "pending" && c.requester_id === currentUserId
  );
  const accepted = connections.filter((c) => c.status === "accepted");

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-24 text-center">
          <p className="text-muted-foreground">Loading connections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="container mx-auto px-4 py-24 max-w-5xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">My Connections</h1>
          <p className="text-muted-foreground">
            Manage your professional network and connection requests
          </p>
        </div>

        <Tabs defaultValue="connections" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="connections" className="gap-2">
              <Users className="h-4 w-4" />
              Connected ({accepted.length})
            </TabsTrigger>
            <TabsTrigger value="received" className="gap-2">
              <Clock className="h-4 w-4" />
              Requests ({pendingReceived.length})
            </TabsTrigger>
            <TabsTrigger value="sent" className="gap-2">
              <UserCheck className="h-4 w-4" />
              Sent ({pendingSent.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="connections" className="mt-6">
            {accepted.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-lg">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No connections yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start connecting with professionals to grow your network
                </p>
                <Button onClick={() => navigate("/connect")}>Browse Profiles</Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {accepted.map((connection) => renderConnectionCard(connection))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="received" className="mt-6">
            {pendingReceived.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-lg">
                <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No pending requests</h3>
                <p className="text-muted-foreground">
                  You don't have any connection requests at the moment
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {pendingReceived.map((connection) => renderConnectionCard(connection, true))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sent" className="mt-6">
            {pendingSent.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-lg">
                <UserCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No sent requests</h3>
                <p className="text-muted-foreground">
                  You haven't sent any connection requests yet
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {pendingSent.map((connection) => (
                  <Card key={connection.id}>
                    {renderConnectionCard(connection)}
                    <CardContent className="pt-0">
                      <Badge variant="outline" className="gap-1">
                        <Clock className="h-3 w-3" />
                        Pending
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
